import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getPlaceholderImage } from '../../utils/placeholder';
import { Note, rich } from '../ui';
import { CheckIcon, CloseIcon, CopyIcon } from '../ui/Icons';
import './ColorThemes.css';
/** Theme-Namen aus dem Backend: "magenta organge" → "Magenta Orange" (nur Anzeige) */
const displayName = (name) => name
    .replace(/\borgange\b/gi, 'orange')
    .replace(/\b\p{L}/gu, (c) => c.toUpperCase());
const ColorThemes = ({ themes }) => {
    const { t } = useTranslation();
    const [copiedId, setCopiedId] = useState(null);
    const [preview, setPreview] = useState(null);
    const timer = useRef(null);
    const closeRef = useRef(null);
    useEffect(() => () => {
        if (timer.current)
            clearTimeout(timer.current);
    }, []);
    useEffect(() => {
        if (!preview)
            return;
        closeRef.current?.focus();
        const onKey = (e) => e.key === 'Escape' && setPreview(null);
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [preview]);
    const copy = useCallback(async (theme) => {
        try {
            await navigator.clipboard.writeText(theme.themeString);
            setCopiedId(theme.id);
            if (timer.current)
                clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopiedId(null), 1800);
        }
        catch {
            // Fallback: Text markieren, damit man ihn manuell kopieren kann
            const el = document.getElementById(`theme-string-${theme.id}`);
            if (el) {
                const range = document.createRange();
                range.selectNodeContents(el);
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(range);
            }
        }
    }, []);
    if (themes.length === 0) {
        return _jsx("p", { className: "product-tab-intro", children: t('pages.themes.empty') });
    }
    const sorted = [...themes].sort((a, b) => a.sortOrder && b.sortOrder ? a.sortOrder - b.sortOrder : a.id - b.id);
    return (_jsxs("div", { className: "themes", children: [_jsxs("p", { className: "product-tab-intro", children: [rich(t('pages.themes.intro')), ' ', _jsx(Link, { to: "/guides/themes", className: "inline-link", children: t('pages.themes.guide') })] }), _jsxs("div", { className: "theme-notes", children: [_jsx(Note, { variant: "warn", label: t('pages.themes.noFieldLabel'), children: _jsx("p", { children: t('pages.themes.noFieldNote') }) }), _jsx(Note, { variant: "tip", label: t('pages.themes.gradientLabel'), children: _jsx("p", { children: t('pages.themes.gradientNote') }) })] }), _jsx("div", { className: "theme-grid", children: sorted.map((theme) => (_jsxs("article", { className: "theme-card", children: [_jsx("button", { type: "button", className: "theme-image", onClick: () => setPreview(theme), "aria-label": `${t('pages.themes.preview')}: ${displayName(theme.name)}`, children: _jsx("img", { src: theme.image, alt: "", loading: "lazy", onError: (e) => {
                                    e.target.src = getPlaceholderImage(theme.name);
                                } }) }), _jsxs("div", { className: "theme-body", children: [_jsx("h3", { className: "theme-name", children: displayName(theme.name) }), _jsxs("div", { className: "theme-string-row", children: [_jsx("code", { id: `theme-string-${theme.id}`, className: "theme-string", children: theme.themeString }), _jsxs("button", { type: "button", className: `btn btn-outline theme-copy ${copiedId === theme.id ? 'done' : ''}`, onClick: () => copy(theme), children: [copiedId === theme.id ? _jsx(CheckIcon, { size: 16 }) : _jsx(CopyIcon, { size: 16 }), copiedId === theme.id ? t('ui.copied') : t('ui.copy')] })] })] })] }, theme.id))) }), preview && (_jsx("div", { className: "theme-preview", role: "dialog", "aria-modal": "true", "aria-label": displayName(preview.name), onClick: () => setPreview(null), children: _jsxs("figure", { onClick: (e) => e.stopPropagation(), children: [_jsx("img", { src: preview.image, alt: displayName(preview.name) }), _jsxs("figcaption", { children: [_jsx("span", { children: displayName(preview.name) }), _jsx("button", { ref: closeRef, type: "button", className: "icon-btn", onClick: () => setPreview(null), "aria-label": t('pages.themes.close'), children: _jsx(CloseIcon, { size: 18 }) })] })] }) }))] }));
};
export default ColorThemes;
